import React from "react";
import { Helmet } from "react-helmet-async";
import sharedConfig, { TLang } from "_configs/sharedConfig";
import { useTranslation } from "react-i18next";
import thumbnail from "../../assets/images/thumbnail.webp";

type Props = {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    alternateUrls?: { lang: TLang; url: string }[];
    noIndex?: boolean;
};

export function Meta(props: Props) {
    const { t } = useTranslation();
    // Un titre vide se traduit par "Sans titre" dans les résultats de recherche : on retombe
    // toujours sur les meta par défaut. Le trim évite les retours à la ligne dans le <title>.
    const title = props.title?.trim() || t("meta.title");
    const description = props.description?.trim() || t("meta.description");
    return (
        <Helmet>
            <title>{title}</title>
            {props.noIndex && <meta name="robots" content="noindex, nofollow" />}
            <meta name="title" content={title} />
            <meta name="og:title" content={title} />
            <meta name="twitter:title" content={title} />

            <meta name="description" content={description} />
            <meta name="og:description" content={description} />
            <meta name="twitter:description" content={description} />

            <meta name="image" content={props.image ?? thumbnail} />
            <meta name="og:image" content={props.image ?? thumbnail} />
            <meta name="twitter:image" content={props.image ?? thumbnail} />

            <meta name="twitter:card" content="summary" />
            <meta name="og:url" content={props.url?.startsWith("http") ? props.url : sharedConfig.appUrl + props.url} />
            {props.url && (
                <link
                    rel="canonical"
                    href={props.url?.startsWith("http") ? props.url : sharedConfig.appUrl + props.url}
                />
            )}
            {props.alternateUrls?.map(({ lang, url }) => {
                return (
                    <link
                        key={lang}
                        rel="alternate"
                        type="text/html"
                        hrefLang={lang}
                        href={url}
                        title={sharedConfig.languages[lang].title}
                    />
                );
            })}
        </Helmet>
    );
}
